type KeyValue = string | number | boolean

export interface DefaultQueryParams {
  search?: string
  page?: number
  page_size?: number
  [key: string]: KeyValue | Iterable<KeyValue>
}

export interface QueryFunction<T, I> {
  (urlParams: T, queryParams?: DefaultQueryParams): Promise<I>
}

export interface MutationFunction<T, I> {
  (urlParams: T, data?: Partial<I>): Promise<I>
}

export interface ActionFunction<T, I, R> {
  (urlParams: T, data: I): Promise<R>
}

export interface PaginatedResponse<T> {
  next: number | null
  prev: number | null
  count: number
  results: T[]
}

export interface UseInfiniteQueryResult<T> {
  pages: PaginatedResponse<T>
}

export interface HandleReturnProps {
  <T>({ status, data }: { status: number; data?: T }): T
}

export interface HttpError<T = unknown> {
  status: number
  data?: T
}

export const handleReturn: HandleReturnProps = ({ status, data }) => {
  if (status >= 200 && status < 300) {
    return data
  } else {
    throw { status, data } satisfies HttpError
  }
}

export const isHttpError = (error: unknown): error is HttpError =>
  Boolean(error) &&
  typeof error === 'object' &&
  'status' in error &&
  typeof (error as HttpError).status === 'number' &&
  !('isAxiosError' in error)

export const toHttpError = (error: unknown): HttpError => {
  if (isHttpError(error)) return error
  const axiosLike = error as {
    isAxiosError?: boolean
    response?: { status?: number; data?: unknown }
  }
  if (axiosLike?.isAxiosError) {
    return {
      status: axiosLike.response?.status ?? 0,
      data: axiosLike.response?.data
    }
  }
  throw error
}
